// @flow

import * as React from "react";
import cx from "classnames";
import bowser from "bowser";

import TableRow from "./TableRow";
import Card from "../Card";
import SvgButton from "../SvgButton";
import ArrowDownIcon from "../../assets/icons/other/bottomCaret.svg";
import ArrowUpIcon from "../../assets/icons/other/upCaret.svg";

import Pagination from "../Pagination";

import styles from "./Table.scss";
import type { ReactElementRef } from "../../types/react";

export type TableHeader = {
  columnId: string,
  label: string,
  descSort?: () => void,
  ascSort?: () => void
};

type TableProps = {
  tableHeaders: Array<TableHeader>,
  getPreviousPage: (page: number) => void,
  getNextPage: (page: number) => void,
  maxPages: number,
  data: Array<{ id: number, string: React.Node }>,
  site: "customer" | "buba" | "bank",
  onInquiryClick: number => void,
  currentPage: number,
  isPaginationHidden: boolean,
  sortedBy: string,
  sortingDirection: "asc" | "desc"
};

class Table extends React.Component<TableProps> {
  static defaultProps = {
    data: [],
    tableHeaders: []
  };

  tableRef: ReactElementRef<HTMLDivElement> = React.createRef();

  buttonRef: ReactElementRef<HTMLButtonElement> = React.createRef();

  componentDidMount() {
    window.addEventListener("mouseover", this.handleMouseOver);
  }

  handleMouseOver = (e: MouseEvent) => {
    if (this.tableRef && this.tableRef.current) {
      const tableNode = this.tableRef.current;
      const { target } = e;
      const isTargetedElementInTable = target instanceof Node && tableNode.contains(target);
      const isFocusedElementInTable = tableNode.contains(document.activeElement);
      const browserName = bowser.getParser(window.navigator.userAgent).getBrowserName();
      const isMicrosoftBrowser = ["Internet Explorer", "Edge"].includes(browserName);

      if (
        isTargetedElementInTable &&
        !isFocusedElementInTable &&
        isMicrosoftBrowser &&
        this.buttonRef &&
        this.buttonRef.current
      ) {
        this.buttonRef.current.focus();
      }
    }
  };

  redirectToInquiry = (id: number) => {
    const { onInquiryClick } = this.props;
    onInquiryClick(id);
  };

  handleKeyDown = (e: KeyboardEvent, id: number) => {
    if (e.key === "Enter") {
      this.redirectToInquiry(id);
    }
  };

  render() {
    const {
      tableHeaders,
      data,
      getPreviousPage,
      getNextPage,
      maxPages,
      site,
      currentPage,
      isPaginationHidden,
      sortedBy,
      sortingDirection
    } = this.props;

    return (
      <Card additionalCardStyles={styles.card}>
        <div className={styles.tableWrapper} ref={this.tableRef}>
          <table className={styles.table}>
            <thead data-testid="table-header">
              <tr>
                {tableHeaders.map((header, index) => (
                  <th key={header.label} scope="col">
                    <span className={styles.label}>{header.label}</span>
                    {header.ascSort && (
                      <SvgButton
                        buttonRef={index === 0 ? this.buttonRef : undefined}
                        className={cx(styles.arrowUp, {
                          [styles.activeArrow]:
                            sortedBy === header.columnId && sortingDirection === "asc"
                        })}
                        src={ArrowUpIcon}
                        alt="Sortierung aufsteigend"
                        aria-label="Sortierung aufsteigend"
                        onClick={header.ascSort}
                        data-testid="sortAsc"
                        hasColorOnFocus={false}
                      />
                    )}
                    {header.descSort && (
                      <SvgButton
                        className={cx(styles.arrowDown, {
                          [styles.activeArrow]:
                            sortedBy === header.columnId && sortingDirection === "desc"
                        })}
                        src={ArrowDownIcon}
                        alt="Sortierung absteigend"
                        aria-label="Sortierung absteigend"
                        onClick={header.descSort}
                        data-testid="sortDesc"
                        hasColorOnFocus={false}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map(rowData => {
                const { id } = rowData;

                return (
                  <TableRow
                    site={site}
                    key={id}
                    onClick={() => this.redirectToInquiry(id)}
                    onKeyDown={e => this.handleKeyDown(e, id)}
                    tableHeaders={tableHeaders}
                    rowData={rowData}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination
          className={styles.pagination}
          currentPage={currentPage}
          getPreviousPage={getPreviousPage}
          getNextPage={getNextPage}
          maxPages={maxPages}
          isPaginationHidden={isPaginationHidden}
        />
      </Card>
    );
  }
}

export default Table;
